import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PrimaryButton, SecondaryButton} from "@/components/Button";
import useResendEmailVerification from "@/hooks/useResendEmailVerification";
import {postBinanceWithdrawalService} from "@/services/wallet/postBinanceWithdrawal.service";
import styles from "./BinanceWithdrawal.module.css";

const MIN_AMOUNT = 10;
const MAX_AMOUNT = 1500;
const FEE = 1;
const NETWORK = "TRX";

const BinanceWithdrawal = ({onBack, onCancel}) => {
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [addressMsg, setAddressMsg] = useState({text: "", color: ""});
  const [amountMsg, setAmountMsg] = useState({text: "", color: ""});
  const [addressTouched, setAddressTouched] = useState(false);
  const [amountTouched, setAmountTouched] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const {t} = useTranslation();
  const {resendVerification, isSuccess: isEmailSent, isError: isEmailError} = useResendEmailVerification();

  useEffect(() => {
    if (isEmailSent) {
      setFeedback({type: "success", text: t("emailVerificationSentMessage")});
    }
    if (isEmailError) {
      setFeedback({type: "error", text: t("emailVerificationErrorMessage")});
    }
  }, [isEmailSent, isEmailError, t]);

  const resetState = () => {
    setAmount("");
    setAddress("");
    setShowConfirm(false);
    setAmountMsg({text: "", color: ""});
    setAddressMsg({text: "", color: ""});
    setAmountTouched(false);
    setAddressTouched(false);
  };

  const isValidTronAddress = addr => {
    if (!addr || typeof addr !== "string") return false;
    if (!addr.startsWith("T")) return false;
    if (addr.length !== 34) return false;
    return true;
  };

  const validateAmount = val => {
    if (!val) return false;
    const num = Number(val);
    if (!Number.isFinite(num)) return false;
    return num >= MIN_AMOUNT && num <= MAX_AMOUNT;
  };

  const isContinueEnabled = isValidTronAddress(address) && validateAmount(amount);

  const handleContinue = () => {
    setAddressTouched(true);
    setAmountTouched(true);
    // Only show modal if no errors
    if (isValidTronAddress(address) && validateAmount(amount)) {
      setShowConfirm(true);
    }
  };

  // Live validation for amount
  const handleAmountChange = e => {
    const val = e.target.value;
    setAmount(val);
    setAmountTouched(true);
    // Only allow valid number format
    const numberRegex = /^\d*\.?\d*$/;
    if (!val) {
      setAmountMsg({text: t("binanceWithdrawalAmountRequired"), color: "var(--status-error)"});
    } else if (!numberRegex.test(val)) {
      setAmountMsg({text: t("binanceWithdrawalInputErrorNumber"), color: "var(--status-error)"});
    } else {
      const num = Number(val);
      if (!Number.isFinite(num)) {
        setAmountMsg({text: t("binanceWithdrawalInputErrorInvalid"), color: "var(--status-error)"});
      } else if (num < MIN_AMOUNT) {
        setAmountMsg({
          text: `${t("binanceWithdrawalInputErrorMin")} ${MIN_AMOUNT} USDT.`,
          color: "var(--status-error)",
        });
      } else if (num > MAX_AMOUNT) {
        setAmountMsg({
          text: `${t("binanceWithdrawalInputErrorMax")} ${MAX_AMOUNT} USDT.`,
          color: "var(--status-error)",
        });
      } else {
        setAmountMsg({text: t("binanceWithdrwalAmountCorrect"), color: "var(--accent-success)"});
      }
    }
  };

  // Live validation for address
  const handleAddressChange = e => {
    const val = e.target.value;
    setAddress(val);
    setAddressTouched(true);
    if (!val) {
      setAddressMsg({text: t("binanceWithdrawalAddressErrorRequired"), color: "var(--status-error)"});
    } else if (!val.startsWith("T")) {
      setAddressMsg({text: t("binanceWithdrawalAddressErrorStart"), color: "var(--status-error)"});
    } else if (val.length !== 34) {
      setAddressMsg({text: t("binanceWithdrawalAddressErrorLength"), color: "var(--status-error)"});
    } else {
      setAddressMsg({text: t("binanceWithdrawalAddressCorrect"), color: "var(--accent-success)"});
    }
  };

  const handleConfirm = async () => {
    if (!isValidTronAddress(address)) {
      setFeedback({type: "error", text: t("binanceWithdrawalAddressErrorRequired")});
      setIsPending(false);
      return;
    }
    setIsPending(true);
    try {
      await postBinanceWithdrawalService({
        amount,
        to_address: address,
        network: NETWORK,
      });
      setFeedback({type: "success", text: `${t("withdrawalSuccessTitle")} — ${amount} USDT`});
      resetState();
      if (typeof onCancel === "function") onCancel();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "";

      if (errorMessage.includes("emailNotVerified")) {
        setFeedback({type: "error", text: t("withdrawalStepsErrorDesc_3")});
        resendVerification();
      } else {
        setFeedback({type: "error", text: errorMessage || t("withdrawalFailureMessage")});
      }
      setShowConfirm(false);
    }
    setIsPending(false);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>{t("binanceWithdrawalTitle")}</h2>

      <div className={styles.infoCard}>
        <div className={styles.infoRow}>
          <p className={styles.infoLabel}>{t("binanceWithdrawalNetwork")}</p>
          <p className={`${styles.infoValue} ${styles.infoValueHighlight}`}>{NETWORK}</p>
        </div>
        <div className={styles.infoRow}>
          <p className={styles.infoLabel}>{t("binanceWithdrawalMinAmount")}</p>
          <p className={styles.infoValue}>{MIN_AMOUNT.toFixed(2)} USDT</p>
        </div>
        <div className={styles.infoRow}>
          <p className={styles.infoLabel}>{t("binanceWithdrawalMaxAmount")}</p>
          <p className={styles.infoValue}>{MAX_AMOUNT.toFixed(2)} USDT</p>
        </div>
        <div className={styles.infoRow}>
          <p className={styles.infoLabel}>{t("binanceWithdrawalCommission")}</p>
          <p className={styles.infoValue}>{FEE.toFixed(2)} USDT</p>
        </div>
      </div>

      {feedback && (
        <div
          className={styles.fieldMessage}
          style={{
            color: feedback.type === "error" ? "var(--status-error)" : "var(--accent-success)",
            textAlign: "center",
            marginBottom: 16,
          }}
        >
          {feedback.text}
        </div>
      )}

      <div className={styles.formFields}>
        <div className={styles.fieldGroup}>
          <input
            className={`${styles.input} ${amountTouched && amountMsg.color === "var(--status-error)" ? styles.hasError : ""} ${amountTouched && amountMsg.color === "var(--accent-success)" ? styles.hasSuccess : ""}`}
            placeholder={`${t("binanceWithdrawalAmount")} (USDT)`}
            value={amount}
            onChange={handleAmountChange}
            onBlur={() => setAmountTouched(true)}
            type="text"
            inputMode="decimal"
          />
          {amountTouched && amountMsg.text && (
            <div
              className={styles.fieldMessage}
              style={{color: amountMsg.color}}
            >
              {amountMsg.text}
            </div>
          )}
        </div>

        <div className={styles.fieldGroup}>
          <input
            className={`${styles.input} ${addressTouched && addressMsg.color === "var(--status-error)" ? styles.hasError : ""} ${addressTouched && addressMsg.color === "var(--accent-success)" ? styles.hasSuccess : ""}`}
            placeholder={t("binanceWithdrawalAddress")}
            value={address}
            onChange={handleAddressChange}
            onBlur={() => setAddressTouched(true)}
            type="text"
          />
          {addressTouched && addressMsg.text && (
            <div
              className={styles.fieldMessage}
              style={{color: addressMsg.color}}
            >
              {addressMsg.text}
            </div>
          )}
        </div>
      </div>

      <div className={styles.actionsBar}>
        <PrimaryButton
          onClick={handleContinue}
          disabled={!isContinueEnabled}
          fullWidth
        >
          {t("binanceWithdrawalContinueBtn")}
        </PrimaryButton>
        <div className={styles.secondaryRow}>
          <SecondaryButton
            onClick={() => {
              resetState();
              if (typeof onBack === "function") onBack();
            }}
          >
            {t("withdrawalStepsBack")}
          </SecondaryButton>
          <SecondaryButton
            onClick={() => {
              resetState();
              onCancel();
            }}
          >
            {t("withdrawalStepsCancel")}
          </SecondaryButton>
        </div>
      </div>

      {showConfirm && (
        <div
          className={styles.confirmOverlay}
          onClick={() => setShowConfirm(false)}
        >
          <div
            className={styles.confirmCard}
            onClick={e => e.stopPropagation()}
          >
            <h3 className={styles.confirmTitle}>{t("binanceWithdrawalConfirmTitle")}</h3>

            <div className={styles.confirmDataCard}>
              <div className={styles.confirmRow}>
                <p className={styles.confirmLabel}>{t("binanceWithdrawalAddressModal")}</p>
                <p className={`${styles.confirmValue} ${styles.success}`}>{address}</p>
              </div>
              <div className={styles.confirmRow}>
                <p className={styles.confirmLabel}>{t("binanceWithdrawalNetwork")}</p>
                <p className={`${styles.confirmValue} ${styles.highlight}`}>{NETWORK}</p>
              </div>
              <div className={styles.confirmRow}>
                <p className={styles.confirmLabel}>{t("binanceWithdrawalAmount")}</p>
                <p className={`${styles.confirmValue} ${styles.success}`}>{Number(amount).toFixed(2)} USDT</p>
              </div>
              <div className={styles.confirmRow}>
                <p className={styles.confirmLabel}>{t("binanceWithdrawalCommission")}</p>
                <p className={`${styles.confirmValue} ${styles.danger}`}>{FEE.toFixed(2)} USDT</p>
              </div>
            </div>

            <div className={styles.confirmActions}>
              <PrimaryButton
                onClick={handleConfirm}
                disabled={isPending}
              >
                {isPending ? "Procesando..." : t("binanceWithdrawalConfirmBtn")}
              </PrimaryButton>
              <SecondaryButton
                onClick={() => {
                  setShowConfirm(false);
                  resetState();
                }}
              >
                {t("withdrawalStepsCancel")}
              </SecondaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

BinanceWithdrawal.propTypes = {
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
};

export default BinanceWithdrawal;
